import { InputBase } from '@mui/material';
import MuiButton from '@mui/material/Button';
import MuiButtonBase from '@mui/material/ButtonBase';
import MuiIconButton from '@mui/material/IconButton';
import { alpha, keyframes, styled } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type {
  NavigationActionContentPosition,
  NavigationActionVisualState,
} from 'components/ui/navigationActionFeedback';
import { getThemeSpacingCssPx, getThemeTypographyCssPx } from 'components/ui/utilities/theme';
import { getCssPx } from 'components/ui/utilities/zoom';
import { getPrismScrollbarStyles, PRISM_BREAKPOINTS, RED } from 'theme';
import type {
  TertiaryNavigationActionButtonVariant,
  TertiaryNavigationIconButtonVariant,
} from './TertiaryNavigation.types';
import { TERTIARY_NAVIGATION_AI_PROMPT_WIDTH_PX } from './TertiaryNavigationAiPrompt.constants';

// locked = final allocation (RED[400])
const NAVIGATION_ACTION_LOCKED_COLOR = RED[400];
// save error (RED[600])
const NAVIGATION_ACTION_ERROR_COLOR = RED[600];
// Keep variant color when disabled; pale via opacity (matches final allocation).
const NAVIGATION_ACTION_DISABLED_OPACITY = 0.65;

interface ZoomStyleProps {
  zoomLevel: number;
}

interface ItemStyleProps extends ZoomStyleProps {
  isDisabled?: boolean;
  isSelected?: boolean;
}

interface IconButtonStyleProps {
  iconButtonVariant: TertiaryNavigationIconButtonVariant;
}

interface ActionButtonStyleProps {
  actionButtonVariant: TertiaryNavigationActionButtonVariant;
  actionButtonState?: NavigationActionVisualState;
  isLocked?: boolean;
}

interface ActionTransitionStyleProps {
  isVisible: boolean;
}

interface SplitMainButtonStyleProps extends ActionButtonStyleProps {
  isFinalAllocationLocked?: boolean;
  isLocked?: boolean;
  splitActionState?: NavigationActionVisualState;
}

interface SplitContentItemStyleProps {
  contentPosition: NavigationActionContentPosition;
}

interface CheckboxActionStyleProps {
  isDisabled?: boolean;
}

interface CurrencySegmentStyleProps {
  segment: 'currency' | 'unit';
}

const TERTIARY_NAVIGATION_TABLET_BREAKPOINT_PX = PRISM_BREAKPOINTS.tablet1024;
// Aligns the tertiary tabs with the workspace canvas after tertiary inner padding.
const TERTIARY_NAVIGATION_HORIZONTAL_MARGIN_PX = 13;
const TERTIARY_NAVIGATION_HORIZONTAL_PADDING_PX = 2;
const shouldForwardZoomProp = (prop: PropertyKey) => prop !== 'zoomLevel';
const shouldForwardItemProp = (prop: PropertyKey) =>
  prop !== 'isDisabled' && prop !== 'isSelected' && prop !== 'zoomLevel';
const shouldForwardIconButtonProp = (prop: PropertyKey) => prop !== 'iconButtonVariant';
const shouldForwardActionButtonProp = (prop: PropertyKey) =>
  prop !== 'actionButtonVariant' && prop !== 'actionButtonState' && prop !== 'isLocked';
const shouldForwardActionTransitionProp = (prop: PropertyKey) => prop !== 'isVisible';
const shouldForwardCheckboxActionProp = (prop: PropertyKey) => prop !== 'isDisabled';
const shouldForwardSplitMainButtonProp = (prop: PropertyKey) =>
  prop !== 'actionButtonVariant' &&
  prop !== 'isFinalAllocationLocked' &&
  prop !== 'isLocked' &&
  prop !== 'splitActionState';
const shouldForwardSplitContentItemProp = (prop: PropertyKey) => prop !== 'contentPosition';
const shouldForwardCurrencySegmentProp = (prop: PropertyKey) => prop !== 'segment';

export const TERTIARY_NAVIGATION_ACTION_VISIBILITY_TRANSITION_MS = 250;
export const TERTIARY_NAVIGATION_ACTION_UNMOUNT_DELAY_MS = TERTIARY_NAVIGATION_ACTION_VISIBILITY_TRANSITION_MS + 100;

const TERTIARY_NAVIGATION_CURRENCY_UNIT_MIN_WIDTH_PX = 30;

const tertiaryNavigationSavePulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 14px 0 var(--tertiary-navigation-save-glow-color);
  }
  50% {
    box-shadow: 0 0 24px 2px var(--tertiary-navigation-save-glow-color);
  }
`;

const tertiaryNavigationSavedCheckDraw = keyframes`
  from {
    stroke-dashoffset: 1;
  }
  to {
    stroke-dashoffset: 0;
  }
`;

const getTertiaryNavigationActionColors = (theme: Theme, actionButtonVariant: TertiaryNavigationActionButtonVariant) =>
  theme.palette.navigation.tertiary.actionButton[actionButtonVariant];

const getTertiaryNavigationActionGlowColor = (colors: ReturnType<typeof getTertiaryNavigationActionColors>) =>
  colors.background === 'transparent' ? colors.border : colors.background;

const getTertiaryNavigationActionDisabledColors = (
  theme: Theme,
  colors: ReturnType<typeof getTertiaryNavigationActionColors>,
  isLocked?: boolean,
  splitActionState: NavigationActionVisualState = 'idle'
) => {
  if (splitActionState === 'error') {
    return {
      backgroundColor: NAVIGATION_ACTION_ERROR_COLOR,
      borderColor: NAVIGATION_ACTION_ERROR_COLOR,
      color: theme.palette.common.white,
    };
  }

  if (isLocked) {
    return {
      backgroundColor: NAVIGATION_ACTION_LOCKED_COLOR,
      borderColor: NAVIGATION_ACTION_LOCKED_COLOR,
      color: theme.palette.common.white,
    };
  }

  if (splitActionState !== 'idle') {
    return {
      backgroundColor: colors.background,
      borderColor: colors.border,
      color: colors.text,
    };
  }

  return {
    backgroundColor: colors.background,
    borderColor: colors.border,
    color: colors.text,
  };
};

const getTertiaryNavigationActionAccentHoverColors = (isErrorState: boolean) => {
  if (isErrorState) {
    return {
      backgroundColor: RED[700],
      borderColor: RED[700],
    };
  }

  return {
    backgroundColor: RED[500],
    borderColor: RED[500],
  };
};

const getTertiaryNavigationActionPulseGlowColor = (
  colors: ReturnType<typeof getTertiaryNavigationActionColors>,
  isErrorState: boolean,
  isLocked?: boolean
) => {
  if (isErrorState) {
    return NAVIGATION_ACTION_ERROR_COLOR;
  }

  if (isLocked) {
    return NAVIGATION_ACTION_LOCKED_COLOR;
  }

  return getTertiaryNavigationActionGlowColor(colors);
};

const getTertiaryNavigationTransition = (theme: Theme) =>
  theme.transitions.create(['background-color', 'border-color', 'color', 'outline-color'], {
    duration: 160,
    easing: theme.transitions.easing.easeInOut,
  });

export const TertiaryNavigationRoot = styled('nav', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme }) => ({
  '--tertiary-navigation-action-height': getCssPx(24),
  '--tertiary-navigation-border-width': getCssPx(1),
  '--tertiary-navigation-focus-outline-width': getCssPx(2),
  '--tertiary-navigation-action-value-font-size': getThemeTypographyCssPx(theme, 'overline', 'fontSize'),
  '--tertiary-navigation-action-value-line-height': getThemeTypographyCssPx(theme, 'overline', 'lineHeight'),
  '--tertiary-navigation-font-size': getThemeTypographyCssPx(theme, 'caption', 'fontSize'),
  '--tertiary-navigation-gap-xxs': getThemeSpacingCssPx(theme, 'xxs'),
  '--tertiary-navigation-gap-s': getThemeSpacingCssPx(theme, 's'),
  '--tertiary-navigation-gap-xs': getThemeSpacingCssPx(theme, 'xs'),
  '--tertiary-navigation-horizontal-margin': getCssPx(TERTIARY_NAVIGATION_HORIZONTAL_MARGIN_PX),
  '--tertiary-navigation-horizontal-padding': getCssPx(TERTIARY_NAVIGATION_HORIZONTAL_PADDING_PX),
  '--tertiary-navigation-icon-size': getCssPx(16),
  '--tertiary-navigation-item-height': getCssPx(32),
  '--tertiary-navigation-line-height': getThemeTypographyCssPx(theme, 'caption', 'lineHeight'),
  '--tertiary-navigation-radius-xs': getCssPx(theme.shape.radius.xs),
  '--tertiary-navigation-scrollbar-thumb-border-width': getCssPx(2),
  '--tertiary-navigation-scrollbar-width': getCssPx(8),
  alignItems: 'center',
  backgroundColor: theme.palette.navigation.tertiary.bar.background,
  boxSizing: 'border-box',
  display: 'flex',
  gap: getThemeSpacingCssPx(theme, 's'),
  height: getCssPx(40),
  justifyContent: 'space-between',
  margin: '0 var(--tertiary-navigation-horizontal-margin)',
  minWidth: 0,
  padding: 'var(--tertiary-navigation-horizontal-padding)',
  width: 'calc(100% - (var(--tertiary-navigation-horizontal-margin) * 2))',
  [`@media (max-width: ${TERTIARY_NAVIGATION_TABLET_BREAKPOINT_PX}px)`]: {
    alignItems: 'stretch',
    flexDirection: 'column',
    height: 'auto',
  },
}));

export const TertiaryNavigationItemsArea = styled('div')({
  alignItems: 'center',
  display: 'flex',
  flex: '1 1 auto',
  gap: 'var(--tertiary-navigation-gap-xs, 4px)',
  minWidth: 0,
  [`@media (max-width: ${TERTIARY_NAVIGATION_TABLET_BREAKPOINT_PX}px)`]: {
    width: '100%',
  },
});

export const TertiaryNavigationItems = styled('div')({
  alignItems: 'center',
  display: 'flex',
  // Shrink-only so leftActions (e.g. the add-view "+") sit next to the last tab
  // instead of being pushed to the far right by a stretched items container.
  flex: '0 1 auto',
  gap: 'var(--tertiary-navigation-gap-xs, 4px)',
  minWidth: 0,
  overflowX: 'auto',
  overflowY: 'hidden',
  position: 'relative',
  scrollBehavior: 'smooth',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
});

export const TertiaryNavigationActiveIndicator = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.navigation.tertiary.item.selectedBackground,
  borderRadius: 'var(--tertiary-navigation-radius-xs, 4px)',
  height: 'var(--tertiary-navigation-item-height, 32px)',
  left: 0,
  opacity: 'var(--active-navigation-indicator-opacity, 0)',
  pointerEvents: 'none',
  position: 'absolute',
  top: 0,
  transform: 'translateX(var(--active-navigation-indicator-x, 0px))',
  transition: theme.transitions.create(['opacity', 'transform', 'width'], {
    duration: 220,
    easing: theme.transitions.easing.easeInOut,
  }),
  width: 'var(--active-navigation-indicator-width, 0px)',
  zIndex: 0,
}));

export const TertiaryNavigationScrollButton = styled('button')(({ theme }) => ({
  alignItems: 'center',
  background: 'transparent',
  border: 0,
  borderRadius: 'var(--tertiary-navigation-radius-xs, 4px)',
  color: theme.palette.navigation.tertiary.item.defaultText,
  cursor: 'pointer',
  display: 'inline-flex',
  flex: '0 0 auto',
  height: 'var(--tertiary-navigation-item-height, 32px)',
  justifyContent: 'center',
  margin: 0,
  padding: 0,
  transition: getTertiaryNavigationTransition(theme),
  width: 'var(--tertiary-navigation-action-height, 24px)',
  '& svg': {
    fontSize: 'var(--tertiary-navigation-icon-size, 16px)',
  },
  '&:hover': {
    backgroundColor: theme.palette.navigation.tertiary.item.hoverBackground,
    color: theme.palette.navigation.tertiary.item.hoverText,
  },
  '&:focus-visible': {
    outline: `var(--tertiary-navigation-focus-outline-width, 2px) solid ${theme.palette.navigation.tertiary.item.focusOutline}`,
    outlineOffset: 'calc(var(--tertiary-navigation-focus-outline-width, 2px) * -1)',
  },
  '&:disabled': {
    color: theme.palette.navigation.tertiary.item.disabledText,
    cursor: 'default',
  },
  '&:disabled:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.navigation.tertiary.item.disabledText,
  },
}));

export const TertiaryNavigationItemRoot = styled('div', {
  shouldForwardProp: shouldForwardItemProp,
})<ItemStyleProps>(({ isDisabled, isSelected, theme }) => ({
  alignItems: 'center',
  backgroundColor: 'transparent',
  borderRadius: getCssPx(theme.shape.radius.xs),
  boxSizing: 'border-box',
  color: isSelected
    ? theme.palette.navigation.tertiary.item.selectedText
    : theme.palette.navigation.tertiary.item.defaultText,
  cursor: isDisabled ? 'not-allowed' : 'pointer',
  display: 'inline-flex',
  flex: '0 0 auto',
  fontSize: 'var(--tertiary-navigation-font-size, 12px)',
  fontWeight: theme.typography.fontWeightMedium,
  gap: getThemeSpacingCssPx(theme, 'xs'),
  height: 'var(--tertiary-navigation-item-height, 32px)',
  letterSpacing: 0,
  lineHeight: 'var(--tertiary-navigation-line-height, 16px)',
  maxWidth: getCssPx(220),
  minWidth: 0,
  opacity: isDisabled ? 1 : undefined,
  padding: getThemeSpacingCssPx(theme, 's'),
  position: 'relative',
  transition: getTertiaryNavigationTransition(theme),
  userSelect: 'none',
  whiteSpace: 'nowrap',
  zIndex: 1,
  ...(isDisabled && {
    color: theme.palette.navigation.tertiary.item.disabledText,
  }),
  '&:hover': !isDisabled && {
    backgroundColor: isSelected ? 'transparent' : theme.palette.navigation.tertiary.item.hoverBackground,
    color: theme.palette.navigation.tertiary.item.hoverText,
  },
  '&:active': !isDisabled && {
    backgroundColor: isSelected ? 'transparent' : theme.palette.navigation.tertiary.item.selectedBackground,
    color: theme.palette.navigation.tertiary.item.selectedText,
  },
  '&:focus-visible': {
    outline: `var(--tertiary-navigation-focus-outline-width, 2px) solid ${theme.palette.navigation.tertiary.item.focusOutline}`,
    outlineOffset: 'calc(var(--tertiary-navigation-focus-outline-width, 2px) * -1)',
  },
}));

export const TertiaryNavigationItemLabel = styled('span')({
  display: 'block',
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const TertiaryNavigationItemIconButton = styled('button')(({ theme }) => ({
  alignItems: 'center',
  background: 'transparent',
  border: 0,
  borderRadius: 'var(--tertiary-navigation-radius-xs, 4px)',
  color: theme.palette.navigation.tertiary.item.menuIcon,
  cursor: 'pointer',
  display: 'inline-flex',
  height: 'var(--tertiary-navigation-icon-size, 16px)',
  justifyContent: 'center',
  margin: 0,
  padding: 0,
  transition: getTertiaryNavigationTransition(theme),
  width: 'var(--tertiary-navigation-icon-size, 16px)',
  '& svg': {
    fontSize: 'var(--tertiary-navigation-icon-size, 16px)',
  },
  '&:hover': {
    backgroundColor: theme.palette.navigation.tertiary.item.hoverBackground,
    color: theme.palette.navigation.tertiary.item.hoverText,
  },
  '&:focus-visible': {
    outline: `var(--tertiary-navigation-focus-outline-width, 2px) solid ${theme.palette.navigation.tertiary.item.focusOutline}`,
    outlineOffset: 'var(--tertiary-navigation-focus-outline-width, 2px)',
  },
}));

export const TertiaryNavigationItemMenuButton = styled(TertiaryNavigationItemIconButton)({});

export const TertiaryNavigationItemCloseButton = styled(TertiaryNavigationItemIconButton)({});

export const TertiaryNavigationLeftActions = styled('div')({
  alignItems: 'center',
  display: 'inline-flex',
  flex: '0 0 auto',
  gap: 'var(--tertiary-navigation-gap-xs, 4px)',
});

export const TertiaryNavigationActions = styled('div')({
  alignItems: 'center',
  display: 'flex',
  flex: '0 0 auto',
  gap: 'var(--tertiary-navigation-gap-s, 8px)',
  justifyContent: 'flex-end',
  minWidth: 0,
  [`@media (max-width: ${TERTIARY_NAVIGATION_TABLET_BREAKPOINT_PX}px)`]: {
    justifyContent: 'flex-start',
    overflowX: 'auto',
    scrollbarWidth: 'none',
    width: '100%',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    // The AI prompt panel is an absolutely positioned descendant, and overflow-x on this strip
    // would clip it. Drop the scroll only while the panel is open; the actions are narrow enough
    // to fit once the user has deliberately opened it, and the scroll returns on close.
    '&:has([data-tertiary-navigation-ai-prompt-open="true"])': {
      overflowX: 'visible',
    },
  },
});

export const TertiaryNavigationActionTransitionRoot = styled('span', {
  shouldForwardProp: shouldForwardActionTransitionProp,
})<ActionTransitionStyleProps>(({ isVisible, theme }) => ({
  display: 'inline-flex',
  flex: '0 0 auto',
  marginLeft: isVisible ? 0 : 'calc(var(--tertiary-navigation-gap-s, 8px) / -2)',
  marginRight: isVisible ? 0 : 'calc(var(--tertiary-navigation-gap-s, 8px) / -2)',
  minWidth: 0,
  opacity: isVisible ? 1 : 0,
  overflow: 'hidden',
  pointerEvents: isVisible ? 'auto' : 'none',
  transition: theme.transitions.create(['margin-left', 'margin-right', 'opacity', 'width'], {
    duration: TERTIARY_NAVIGATION_ACTION_VISIBILITY_TRANSITION_MS,
    easing: theme.transitions.easing.easeInOut,
  }),
  width: isVisible ? 'var(--tertiary-navigation-action-transition-width, max-content)' : 0,
  willChange: 'margin-left, margin-right, opacity, width',
}));

export const TertiaryNavigationActionTransitionContent = styled('span')({
  alignItems: 'center',
  display: 'inline-flex',
  minWidth: 'max-content',
  whiteSpace: 'nowrap',
});

export const TertiaryNavigationIconButtonRoot = styled(MuiIconButton, {
  shouldForwardProp: shouldForwardIconButtonProp,
})<IconButtonStyleProps>(({ iconButtonVariant, theme }) => {
  const size =
    iconButtonVariant === 'add'
      ? 'var(--tertiary-navigation-item-height, 32px)'
      : 'var(--tertiary-navigation-action-height, 24px)';

  return {
    backgroundColor:
      iconButtonVariant === 'menu'
        ? theme.palette.navigation.tertiary.iconButton.menuBackground
        : theme.palette.navigation.tertiary.iconButton.background,
    borderRadius: 'var(--tertiary-navigation-radius-xs, 4px)',
    color: theme.palette.navigation.tertiary.iconButton.color,
    height: size,
    padding: 0,
    transition: getTertiaryNavigationTransition(theme),
    width: size,
    '& svg': {
      fontSize: 'var(--tertiary-navigation-icon-size, 16px)',
    },
    '&:hover': {
      backgroundColor: theme.palette.navigation.tertiary.iconButton.hoverBackground,
      color: theme.palette.navigation.tertiary.iconButton.hoverColor,
    },
    '&.Mui-disabled': {
      color: theme.palette.navigation.tertiary.iconButton.disabledColor,
    },
  };
});

export const TertiaryNavigationIconTooltipAnchor = styled('span')({
  display: 'inline-flex',
  flex: '0 0 auto',
});

export const TertiaryNavigationSearchRoot = styled('div')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.neutral[200],
  border: `var(--tertiary-navigation-border-width, 1px) solid ${theme.palette.neutral[500]}`,
  borderRadius: 'var(--tertiary-navigation-radius-xs, 4px)',
  boxSizing: 'border-box',
  color: theme.palette.neutral[800],
  display: 'inline-grid',
  flex: '0 0 auto',
  gap: 'var(--tertiary-navigation-gap-xxs, 2px)',
  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
  gridTemplateRows: 'minmax(0, 1fr)',
  height: 'var(--tertiary-navigation-action-height, 24px)',
  maxHeight: 'var(--tertiary-navigation-action-height, 24px)',
  minWidth: 0,
  padding: '0 var(--tertiary-navigation-gap-s, 8px)',
  position: 'relative',
  transition: theme.transitions.create(['border-color', 'color', 'gap', 'grid-template-columns', 'padding', 'width'], {
    duration: theme.transitions.duration.short,
    easing: theme.transitions.easing.easeInOut,
  }),
  width: `calc(var(--tertiary-navigation-action-height, 24px) * ${200 / 24})`,
  '&:focus-within': {
    borderColor: theme.palette.brand[400],
    color: theme.palette.brand[700],
    width: `calc(var(--tertiary-navigation-action-height, 24px) * ${280 / 24})`,
  },
  '&[data-collapsing="true"]': {
    gap: 0,
    gridTemplateColumns: 'var(--tertiary-navigation-action-height, 24px) minmax(0, 0fr) minmax(0, 0fr)',
    justifyItems: 'center',
    overflow: 'hidden',
    padding: 0,
    width: 'var(--tertiary-navigation-action-height, 24px)',
  },
  '& .MuiInputBase-root, & [data-tertiary-navigation-search-trailing-actions="true"]': {
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.short,
    }),
  },
  '&[data-collapsing="true"] .MuiInputBase-root, &[data-collapsing="true"] [data-tertiary-navigation-search-trailing-actions="true"]':
    {
      opacity: 0,
      pointerEvents: 'none',
      visibility: 'hidden',
      width: 0,
    },
}));

export const TertiaryNavigationSearchTrailingActions = styled('span')({
  alignItems: 'center',
  display: 'inline-flex',
  gap: 'var(--tertiary-navigation-gap-xs, 4px)',
  justifyContent: 'flex-end',
  justifySelf: 'end',
  minWidth: 0,
});

export const TertiaryNavigationSearchSuggestions = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  border: `var(--tertiary-navigation-border-width, 1px) solid ${theme.palette.neutral[300]}`,
  borderRadius: 'var(--tertiary-navigation-radius-xs, 4px)',
  boxShadow: theme.shadows[4],
  boxSizing: 'border-box',
  display: 'grid',
  gap: 'var(--tertiary-navigation-gap-xxs, 2px)',
  maxHeight: `calc(var(--tertiary-navigation-action-height, 24px) * ${240 / 24})`,
  minWidth: `calc(var(--tertiary-navigation-action-height, 24px) * ${280 / 24})`,
  overscrollBehavior: 'contain',
  overflowY: 'auto',
  padding: 'var(--tertiary-navigation-gap-xs, 4px)',
  position: 'absolute',
  right: 0,
  scrollbarGutter: 'auto',
  top: 'calc(100% + var(--tertiary-navigation-gap-xs, 4px))',
  width: `calc(var(--tertiary-navigation-action-height, 24px) * ${320 / 24})`,
  zIndex: theme.zIndex.modal,
  ...getPrismScrollbarStyles(theme, {
    thumbBorderColor: theme.palette.common.white,
    thumbBorderWidth: 'var(--tertiary-navigation-scrollbar-thumb-border-width, 2px)',
    width: 'var(--tertiary-navigation-scrollbar-width, 8px)',
  }),
}));

export const TertiaryNavigationSearchSuggestionItem = styled('button')(({ theme }) => ({
  '--tertiary-navigation-search-suggestion-icon-background': 'transparent',
  '--tertiary-navigation-search-suggestion-icon-shadow': 'none',
  alignItems: 'flex-start',
  backgroundColor: 'transparent',
  border: 0,
  borderRadius: 'var(--tertiary-navigation-radius-xs, 4px)',
  boxSizing: 'border-box',
  color: theme.palette.neutral[900],
  cursor: 'pointer',
  display: 'grid',
  gap: 'var(--tertiary-navigation-gap-xxs, 2px)',
  gridTemplateColumns: 'auto minmax(0, 1fr)',
  minHeight: 'var(--tertiary-navigation-item-height, 32px)',
  minWidth: 0,
  padding: 'var(--tertiary-navigation-gap-xs, 4px) var(--tertiary-navigation-gap-s, 8px)',
  textAlign: 'left',
  transition: getTertiaryNavigationTransition(theme),
  width: '100%',
  '&:hover, &:focus-visible, &[data-highlighted="true"]': {
    '--tertiary-navigation-search-suggestion-icon-background': alpha(theme.palette.neutral.white, 0.76),
    '--tertiary-navigation-search-suggestion-icon-shadow': `inset 0 0 0 var(--tertiary-navigation-border-width, 1px) ${alpha(theme.palette.neutral[900], 0.1)}`,
    backgroundColor: theme.palette.navigation.primary.menu.item.hoverBackground,
    color: theme.palette.navigation.primary.menu.item.primaryText,
    outline: 'none',
  },
}));

export const TertiaryNavigationSearchSuggestionIcon = styled('span')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: 'var(--tertiary-navigation-search-suggestion-icon-background)',
  borderRadius: 'var(--tertiary-navigation-radius-xs, 4px)',
  boxShadow: 'var(--tertiary-navigation-search-suggestion-icon-shadow)',
  boxSizing: 'border-box',
  display: 'inline-flex',
  fontSize: 'var(--tertiary-navigation-icon-size, 16px)',
  height: 'var(--tertiary-navigation-line-height, 16px)',
  justifyContent: 'center',
  transition: theme.transitions.create(['background-color', 'box-shadow'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: 'var(--tertiary-navigation-icon-size, 16px)',
  '& svg': {
    fontSize: 'var(--tertiary-navigation-icon-size, 16px)',
  },
}));

export const TertiaryNavigationSearchSuggestionContent = styled('span')({
  display: 'grid',
  gap: 'var(--tertiary-navigation-gap-xxs, 2px)',
  minWidth: 0,
});

export const TertiaryNavigationSearchSuggestionLabel = styled('span')(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: 'var(--tertiary-navigation-font-size, 12px)',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'var(--tertiary-navigation-line-height, 16px)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const TertiaryNavigationSearchSuggestionHighlight = styled('mark')(({ theme }) => ({
  backgroundColor: alpha(theme.palette.warning.main, 0.18),
  borderRadius: 'var(--tertiary-navigation-radius-xs, 4px)',
  color: 'inherit',
  fontWeight: 'inherit',
  padding: '0 var(--tertiary-navigation-gap-xxs, 2px)',
}));

export const TertiaryNavigationSearchSuggestionMetadata = styled('span')({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--tertiary-navigation-gap-xxs, 2px)',
  minWidth: 0,
});

export const TertiaryNavigationSearchSuggestionPillRoot = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.neutral[200],
  border: `var(--tertiary-navigation-border-width, 1px) solid ${theme.palette.neutral[200]}`,
  borderRadius: 'calc(var(--tertiary-navigation-radius-xs, 4px) * 2)',
  color: theme.palette.neutral[800],
  display: 'inline-block',
  fontFamily: theme.typography.fontFamily,
  fontSize: 'var(--tertiary-navigation-action-value-font-size, 10px)',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'var(--tertiary-navigation-action-value-line-height, 14px)',
  maxWidth: `calc(var(--tertiary-navigation-action-height, 24px) * ${120 / 24})`,
  minWidth: 0,
  overflow: 'hidden',
  padding: '0 var(--tertiary-navigation-gap-xs, 4px)',
  textOverflow: 'ellipsis',
  transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  whiteSpace: 'nowrap',
  '&[data-tertiary-navigation-search-suggestion-pill-tone="brand"]': {
    backgroundColor: theme.palette.brand[100],
    borderColor: 'transparent',
    color: theme.palette.neutral[600],
  },
}));

export const TertiaryNavigationSearchSuggestionDescription = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[700],
  fontFamily: theme.typography.fontFamily,
  fontSize: 'var(--tertiary-navigation-action-value-font-size, 10px)',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'var(--tertiary-navigation-action-value-line-height, 14px)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const TertiaryNavigationSearchSuggestionsStatus = styled('div')(({ theme }) => ({
  color: theme.palette.neutral[700],
  fontFamily: theme.typography.fontFamily,
  fontSize: 'var(--tertiary-navigation-font-size, 12px)',
  lineHeight: 'var(--tertiary-navigation-line-height, 16px)',
  padding: 'var(--tertiary-navigation-gap-s, 8px)',
}));

export const TertiaryNavigationSearchIcon = styled('span')({
  alignItems: 'center',
  display: 'inline-flex',
  flex: '0 0 auto',
  height: 'var(--tertiary-navigation-icon-size, 16px)',
  justifyContent: 'center',
  justifySelf: 'center',
  width: 'var(--tertiary-navigation-icon-size, 16px)',
  '& img': {
    display: 'block',
    height: '100%',
    width: '100%',
  },
  '& svg': {
    fontSize: 'var(--tertiary-navigation-icon-size, 16px)',
  },
});

export const TertiaryNavigationSearchLoader = styled('span')(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.brand[600],
  display: 'inline-flex',
  flex: '0 0 auto',
  fontSize: 'var(--tertiary-navigation-icon-size, 16px)',
  height: 'var(--tertiary-navigation-icon-size, 16px)',
  justifyContent: 'center',
  width: 'var(--tertiary-navigation-icon-size, 16px)',
}));

export const TertiaryNavigationSearchClearButton = styled('button')(({ theme }) => ({
  alignItems: 'center',
  appearance: 'none',
  backgroundColor: 'transparent',
  border: 0,
  borderRadius: '50%',
  color: theme.palette.neutral[500],
  cursor: 'pointer',
  display: 'inline-flex',
  flex: '0 0 auto',
  height: 'calc(var(--tertiary-navigation-action-height, 24px) - var(--tertiary-navigation-gap-xs, 4px))',
  justifyContent: 'center',
  padding: 0,
  transition: theme.transitions.create(['background-color', 'color', 'opacity'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: 'calc(var(--tertiary-navigation-action-height, 24px) - var(--tertiary-navigation-gap-xs, 4px))',
  '& svg': {
    fontSize: 'var(--tertiary-navigation-icon-size, 16px)',
  },
  '&:hover, &:focus-visible': {
    backgroundColor: theme.palette.neutral[300],
    color: theme.palette.brand[700],
    outline: 'none',
  },
}));

export const TertiaryNavigationSearchSubmitHint = styled('button')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.common.white,
  border: `var(--tertiary-navigation-border-width, 1px) solid ${theme.palette.neutral[200]}`,
  borderRadius: 'var(--tertiary-navigation-radius-xs, 4px)',
  color: theme.palette.neutral[500],
  cursor: 'pointer',
  display: 'inline-flex',
  flex: '0 0 auto',
  fontFamily: theme.typography.fontFamily,
  fontSize: 'var(--tertiary-navigation-action-value-font-size, 10px)',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'var(--tertiary-navigation-action-value-line-height, 14px)',
  padding: 'var(--tertiary-navigation-gap-xxs, 2px) var(--tertiary-navigation-gap-xs, 4px)',
  transform: 'translateY(0)',
  transition: theme.transitions.create(['background-color', 'border-color', 'color', 'transform'], {
    duration: 120,
    easing: theme.transitions.easing.easeInOut,
  }),
  whiteSpace: 'nowrap',
  '&:hover, &:focus-visible': {
    borderColor: theme.palette.brand[300],
    color: theme.palette.brand[700],
    outline: 'none',
  },
  '&:active, &[data-active="true"]': {
    backgroundColor: theme.palette.brand[100],
    borderColor: theme.palette.brand[400],
    color: theme.palette.brand[800],
    transform: 'translateY(1px)',
  },
}));

export const TertiaryNavigationSearchInput = styled(InputBase)(({ theme }) => ({
  color: theme.palette.neutral[800],
  fontSize: 'var(--tertiary-navigation-font-size, 12px)',
  justifySelf: 'stretch',
  lineHeight: 'var(--tertiary-navigation-line-height, 16px)',
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',
  width: '100%',
  '& .MuiInputBase-input': {
    boxSizing: 'border-box',
    height: 'var(--tertiary-navigation-line-height, 16px)',
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
    padding: 0,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    '&::placeholder': {
      color: theme.palette.neutral[800],
      opacity: 1,
      textOverflow: 'ellipsis',
    },
  },
}));

export const TertiaryNavigationAiButtonRoot = styled(MuiIconButton)(({ theme }) => ({
  background: theme.palette.navigation.tertiary.aiButton.background,
  borderRadius: 'var(--tertiary-navigation-radius-xs, 4px)',
  boxShadow: 'none',
  boxSizing: 'border-box',
  color: theme.palette.navigation.tertiary.aiButton.text,
  height: 'var(--tertiary-navigation-action-height, 24px)',
  isolation: 'isolate',
  overflow: 'hidden',
  padding: 'var(--tertiary-navigation-gap-xxs, 2px)',
  position: 'relative',
  transition: getTertiaryNavigationTransition(theme),
  width: 'var(--tertiary-navigation-action-height, 24px)',
  '&::before': {
    background: theme.palette.navigation.tertiary.aiButton.hoverBackground,
    content: '""',
    inset: 0,
    opacity: 0,
    position: 'absolute',
    transition: theme.transitions.create('opacity', {
      duration: 160,
      easing: theme.transitions.easing.easeInOut,
    }),
    zIndex: -1,
  },
  '& svg': {
    fontSize: 'var(--tertiary-navigation-icon-size, 16px)',
  },
  '&:hover::before, &:focus-visible::before': {
    opacity: 1,
  },
}));

export const TertiaryNavigationAiIcon = styled('img')({
  display: 'block',
  height: 'var(--tertiary-navigation-icon-size, 16px)',
  pointerEvents: 'none',
  width: 'var(--tertiary-navigation-icon-size, 16px)',
});

export const TertiaryNavigationAiPromptRoot = styled('span')({
  display: 'inline-flex',
  flex: '0 0 auto',
  // Containing block for the floating panel. A portal is not an option here: the layout's
  // ShellChrome applies a CSS `zoom`, which a body-level portal would not inherit.
  position: 'relative',
});

export const TertiaryNavigationAiPromptPanel = styled('div')(({ theme }) => ({
  // flex-start is what keeps the AI mark and the submit hint pinned to the first line as the
  // textarea grows downward.
  alignItems: 'flex-start',
  backgroundColor: theme.palette.common.white,
  border: `var(--tertiary-navigation-border-width, 1px) solid ${theme.palette.neutral[300]}`,
  borderRadius: getCssPx(theme.shape.radius.l),
  boxShadow: theme.shadows[4],
  boxSizing: 'border-box',
  display: 'grid',
  gap: 'var(--tertiary-navigation-gap-s, 8px)',
  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
  insetInlineEnd: 0,
  maxWidth: 'calc(100vw - var(--tertiary-navigation-horizontal-margin, 8px) * 4)',
  padding: 'var(--tertiary-navigation-gap-s, 8px)',
  position: 'absolute',
  top: 'calc(100% + var(--tertiary-navigation-gap-xs, 4px))',
  // Expressed against the action height so the panel scales with the zoom level, the same way
  // the search suggestions list does.
  width: `calc(var(--tertiary-navigation-action-height, 24px) * ${TERTIARY_NAVIGATION_AI_PROMPT_WIDTH_PX / 24})`,
  zIndex: theme.zIndex.modal,
  [`@media (max-width: ${TERTIARY_NAVIGATION_TABLET_BREAKPOINT_PX}px)`]: {
    // The bar stacks and the actions row becomes full width and left-aligned, so anchoring to
    // the right edge of a leading control would push the panel off-screen.
    insetInlineEnd: 'auto',
    insetInlineStart: 0,
  },
}));

export const TertiaryNavigationAiPromptIcon = styled('span')({
  alignItems: 'center',
  display: 'inline-flex',
  flex: '0 0 auto',
  height: 'var(--tertiary-navigation-icon-size, 16px)',
  justifyContent: 'center',
  width: 'var(--tertiary-navigation-icon-size, 16px)',
  '& img': {
    display: 'block',
    height: '100%',
    width: '100%',
  },
  '& svg': {
    fontSize: 'var(--tertiary-navigation-icon-size, 16px)',
  },
});

export const TertiaryNavigationAiPromptInput = styled(InputBase)(({ theme }) => ({
  color: theme.palette.neutral[800],
  fontSize: 'var(--tertiary-navigation-font-size, 12px)',
  lineHeight: 'var(--tertiary-navigation-line-height, 16px)',
  minWidth: 0,
  padding: 0,
  width: '100%',
  '& .MuiInputBase-input': {
    boxSizing: 'border-box',
    minWidth: 0,
    padding: 0,
    // No fixed height, nowrap or ellipsis here (unlike the search input): all three would
    // defeat the auto-growing textarea.
    resize: 'none',
    width: '100%',
    '&::placeholder': {
      color: theme.palette.neutral[500],
      opacity: 1,
    },
  },
}));

export const TertiaryNavigationAiPromptActions = styled('span')({
  alignItems: 'center',
  display: 'inline-flex',
  flex: '0 0 auto',
  gap: 'var(--tertiary-navigation-gap-xs, 4px)',
  justifyContent: 'flex-end',
  minWidth: 0,
});

export const TertiaryNavigationAiPromptBusyHint = styled('span')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.brand[100],
  border: `var(--tertiary-navigation-border-width, 1px) solid ${theme.palette.brand[300]}`,
  borderRadius: 'var(--tertiary-navigation-radius-xs, 4px)',
  color: theme.palette.brand[700],
  display: 'inline-flex',
  flex: '0 0 auto',
  fontFamily: theme.typography.fontFamily,
  fontSize: 'var(--tertiary-navigation-action-value-font-size, 10px)',
  fontWeight: theme.typography.fontWeightRegular,
  gap: 'var(--tertiary-navigation-gap-xxs, 2px)',
  lineHeight: 'var(--tertiary-navigation-action-value-line-height, 14px)',
  // Same box metrics as the submit hint it replaces, so the panel does not reflow on the swap.
  padding: 'var(--tertiary-navigation-gap-xxs, 2px) var(--tertiary-navigation-gap-xs, 4px)',
  whiteSpace: 'nowrap',
}));

export const TertiaryNavigationCheckboxActionRoot = styled('label', {
  shouldForwardProp: shouldForwardCheckboxActionProp,
})<CheckboxActionStyleProps>(({ isDisabled = false, theme }) => ({
  '--checkbox-selection-size': 'var(--tertiary-navigation-icon-size, 16px)',
  alignItems: 'center',
  boxSizing: 'border-box',
  color: isDisabled ? theme.palette.navigation.tertiary.item.disabledText : theme.palette.text.secondary,
  cursor: isDisabled ? 'default' : 'pointer',
  display: 'inline-flex',
  flexShrink: 0,
  gap: 'var(--tertiary-navigation-gap-xs, 4px)',
  height: 'var(--tertiary-navigation-action-height, 24px)',
  minWidth: 0,
  transition: getTertiaryNavigationTransition(theme),
}));

export const TertiaryNavigationCheckboxActionLabel = styled('span')(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: 'var(--tertiary-navigation-font-size, 12px)',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'var(--tertiary-navigation-line-height, 16px)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const TertiaryNavigationCurrencySelectorRoot = styled('div')({
  alignItems: 'center',
  borderRadius: 'var(--tertiary-navigation-radius-xs, 4px)',
  boxSizing: 'border-box',
  display: 'inline-flex',
  flexShrink: 0,
  height: 'var(--tertiary-navigation-action-height, 24px)',
  overflow: 'hidden',
});

export const TertiaryNavigationCurrencySegment = styled(MuiButtonBase, {
  shouldForwardProp: shouldForwardCurrencySegmentProp,
})<CurrencySegmentStyleProps>(({ segment, theme }) => ({
  alignItems: 'center',
  backgroundColor:
    segment === 'currency'
      ? theme.palette.navigation.tertiary.currencySelector.background
      : theme.palette.navigation.tertiary.currencySelector.unitBackground,
  border:
    segment === 'currency'
      ? `var(--tertiary-navigation-border-width, 1px) solid ${theme.palette.navigation.tertiary.currencySelector.border}`
      : 'var(--tertiary-navigation-border-width, 1px) solid transparent',
  borderBottomLeftRadius: segment === 'currency' ? 'var(--tertiary-navigation-radius-xs, 4px)' : 0,
  borderBottomRightRadius: segment === 'unit' ? 'var(--tertiary-navigation-radius-xs, 4px)' : 0,
  borderTopLeftRadius: segment === 'currency' ? 'var(--tertiary-navigation-radius-xs, 4px)' : 0,
  borderTopRightRadius: segment === 'unit' ? 'var(--tertiary-navigation-radius-xs, 4px)' : 0,
  color:
    segment === 'currency'
      ? theme.palette.navigation.tertiary.currencySelector.valueText
      : theme.palette.navigation.tertiary.currencySelector.unitText,
  display: 'inline-flex',
  fontFamily: theme.typography.fontFamily,
  fontSize: 'var(--tertiary-navigation-action-value-font-size, 10px)',
  fontWeight: theme.typography.fontWeightRegular,
  gap: 'var(--tertiary-navigation-gap-xxs, 2px)',
  height: '100%',
  lineHeight: 'var(--tertiary-navigation-action-value-line-height, 14px)',
  maxWidth: `calc(var(--tertiary-navigation-action-height, 24px) * ${segment === 'currency' ? 5 : 4})`,
  minWidth:
    segment === 'unit'
      ? `calc(var(--tertiary-navigation-action-height, 24px) * ${TERTIARY_NAVIGATION_CURRENCY_UNIT_MIN_WIDTH_PX / 24})`
      : 0,
  padding: '0 var(--tertiary-navigation-gap-xs, 4px)',
  textTransform: 'none',
  transition: getTertiaryNavigationTransition(theme),
  '& svg': {
    fontSize: 'var(--tertiary-navigation-icon-size, 16px)',
  },
  '&:hover': {
    backgroundColor:
      segment === 'currency'
        ? theme.palette.navigation.tertiary.currencySelector.hoverBackground
        : theme.palette.navigation.tertiary.currencySelector.unitHoverBackground,
  },
}));

const tertiaryNavigationTruncatedText = {
  display: 'inline-block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const TertiaryNavigationCurrencyValue = styled('span')(tertiaryNavigationTruncatedText);

export const TertiaryNavigationCurrencyUnit = styled('span')(tertiaryNavigationTruncatedText);

export const TertiaryNavigationActionButtonRoot = styled(MuiButton, {
  shouldForwardProp: shouldForwardActionButtonProp,
})<ActionButtonStyleProps>(({ actionButtonState = 'idle', actionButtonVariant, isLocked, theme }) => {
  const colors = getTertiaryNavigationActionColors(theme, actionButtonVariant);
  const disabledColors = getTertiaryNavigationActionDisabledColors(theme, colors, isLocked, actionButtonState);
  const isPulseVisible = actionButtonState === 'saving';
  const isErrorState = actionButtonState === 'error';
  const useLockedAccent = Boolean(isLocked && !isErrorState);
  const accentColor = isErrorState ? NAVIGATION_ACTION_ERROR_COLOR : NAVIGATION_ACTION_LOCKED_COLOR;
  const useAccentColors = useLockedAccent || isErrorState;
  const pulseGlowColor = getTertiaryNavigationActionPulseGlowColor(colors, isErrorState, isLocked);
  const accentHoverColors = getTertiaryNavigationActionAccentHoverColors(isErrorState);

  return {
    '--tertiary-navigation-save-glow-color': alpha(pulseGlowColor, 0.5),
    animation: isPulseVisible ? `${tertiaryNavigationSavePulse} 1200ms ease-in-out infinite` : undefined,
    backgroundColor: useAccentColors ? accentColor : colors.background,
    border: `var(--tertiary-navigation-border-width, 1px) solid ${useAccentColors ? accentColor : colors.border}`,
    borderRadius: 'var(--tertiary-navigation-radius-xs, 4px)',
    boxSizing: 'border-box',
    boxShadow: isPulseVisible ? `0 0 20px 0 ${alpha(pulseGlowColor, 0.5)}` : 'none',
    color: useAccentColors ? theme.palette.common.white : colors.text,
    fontFamily: theme.typography.fontFamily,
    fontSize: 'var(--tertiary-navigation-font-size, 12px)',
    fontWeight: theme.typography.fontWeightMedium,
    height: 'var(--tertiary-navigation-action-height, 24px)',
    letterSpacing: 0,
    lineHeight: 'var(--tertiary-navigation-line-height, 16px)',
    minHeight: 'unset',
    minWidth: 'unset',
    overflow: 'hidden',
    padding: '0 var(--tertiary-navigation-gap-s, 8px)',
    textTransform: 'none',
    transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'color', 'outline-color'], {
      duration: 240,
      easing: theme.transitions.easing.easeInOut,
    }),
    whiteSpace: 'nowrap',
    '&:hover': (() => {
      if (isErrorState) {
        return {
          backgroundColor: accentHoverColors.backgroundColor,
          borderColor: accentHoverColors.borderColor,
          color: theme.palette.common.white,
        };
      }

      if (useLockedAccent) {
        return {
          backgroundColor: accentHoverColors.backgroundColor,
          borderColor: accentHoverColors.borderColor,
          color: theme.palette.common.white,
        };
      }

      return {
        backgroundColor: colors.hoverBackground,
        borderColor: colors.hoverBorder,
        color: 'hoverText' in colors ? colors.hoverText : colors.text,
      };
    })(),
    '&.Mui-disabled': {
      backgroundColor: disabledColors.backgroundColor,
      borderColor: disabledColors.borderColor,
      color: disabledColors.color,
      opacity: NAVIGATION_ACTION_DISABLED_OPACITY,
    },
  };
});

export const TertiaryNavigationSplitActionRoot = styled('span')({
  display: 'inline-flex',
  flexShrink: 0,
  height: 'var(--tertiary-navigation-action-height, 24px)',
});

export const TertiaryNavigationSplitMainButton = styled(MuiButton, {
  shouldForwardProp: shouldForwardSplitMainButtonProp,
})<SplitMainButtonStyleProps>(({
  actionButtonVariant,
  isFinalAllocationLocked,
  isLocked,
  splitActionState = 'idle',
  theme,
}) => {
  const colors = getTertiaryNavigationActionColors(theme, actionButtonVariant);
  const disabledColors = getTertiaryNavigationActionDisabledColors(theme, colors, isLocked, splitActionState);
  const isErrorState = splitActionState === 'error';
  const useFinalAllocationAccent = Boolean(isLocked && !isErrorState);
  const accentColor = isErrorState ? NAVIGATION_ACTION_ERROR_COLOR : NAVIGATION_ACTION_LOCKED_COLOR;
  const useAccentColors = useFinalAllocationAccent || isErrorState;
  const isPulseVisible = splitActionState === 'saving';
  const pulseGlowColor = getTertiaryNavigationActionPulseGlowColor(colors, isErrorState, isLocked);
  const accentHoverColors = getTertiaryNavigationActionAccentHoverColors(isErrorState);

  return {
    '--tertiary-navigation-save-glow-color': alpha(pulseGlowColor, 0.5),
    animation: isPulseVisible ? `${tertiaryNavigationSavePulse} 1200ms ease-in-out infinite` : undefined,
    backgroundColor: useAccentColors ? accentColor : colors.background,
    border: `var(--tertiary-navigation-border-width, 1px) solid ${useAccentColors ? accentColor : colors.border}`,
    borderBottomLeftRadius: 'var(--tertiary-navigation-radius-xs, 4px)',
    borderBottomRightRadius: 0,
    borderTopLeftRadius: 'var(--tertiary-navigation-radius-xs, 4px)',
    borderTopRightRadius: 0,
    boxSizing: 'border-box',
    boxShadow: isPulseVisible ? `0 0 20px 0 ${alpha(pulseGlowColor, 0.5)}` : 'none',
    color: useAccentColors ? theme.palette.common.white : colors.text,
    fontFamily: theme.typography.fontFamily,
    fontSize: 'var(--tertiary-navigation-font-size, 12px)',
    fontWeight: theme.typography.fontWeightMedium,
    height: '100%',
    letterSpacing: 0,
    lineHeight: 'var(--tertiary-navigation-line-height, 16px)',
    minHeight: 'unset',
    minWidth: 'unset',
    overflow: 'hidden',
    padding: '0 var(--tertiary-navigation-gap-s, 8px)',
    pointerEvents: splitActionState !== 'idle' ? 'none' : undefined,
    cursor: isFinalAllocationLocked ? 'not-allowed' : 'pointer',
    textTransform: 'none',
    transition: theme.transitions.create(
      ['background-color', 'border-color', 'border-radius', 'box-shadow', 'color', 'outline-color'],
      {
        duration: 240,
        easing: theme.transitions.easing.easeInOut,
      }
    ),
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: useAccentColors ? accentHoverColors.backgroundColor : colors.hoverBackground,
      borderColor: useAccentColors ? accentHoverColors.borderColor : colors.hoverBorder,
      color: useAccentColors ? theme.palette.common.white : ('hoverText' in colors && colors.hoverText) || colors.text,
    },
    '&.Mui-disabled': {
      backgroundColor: disabledColors.backgroundColor,
      borderColor: disabledColors.borderColor,
      color: disabledColors.color,
      opacity: NAVIGATION_ACTION_DISABLED_OPACITY,
    },
  };
});

export const TertiaryNavigationSplitMenuButton = styled(MuiButton, {
  shouldForwardProp: shouldForwardSplitMainButtonProp,
})<SplitMainButtonStyleProps>(({
  actionButtonVariant,
  isFinalAllocationLocked,
  isLocked,
  splitActionState = 'idle',
  theme,
}) => {
  const colors = getTertiaryNavigationActionColors(theme, actionButtonVariant);
  const disabledColors = getTertiaryNavigationActionDisabledColors(theme, colors, isLocked, splitActionState);
  const isErrorState = splitActionState === 'error';
  const useFinalAllocationAccent = Boolean(isLocked && !isErrorState);
  const accentColor = isErrorState ? NAVIGATION_ACTION_ERROR_COLOR : NAVIGATION_ACTION_LOCKED_COLOR;
  const useAccentColors = useFinalAllocationAccent || isErrorState;
  const isActionState = splitActionState !== 'idle';
  const accentHoverColors = getTertiaryNavigationActionAccentHoverColors(isErrorState);

  return {
    backgroundColor: useAccentColors ? accentColor : colors.background,
    border: `var(--tertiary-navigation-border-width, 1px) solid ${useAccentColors ? accentColor : colors.border}`,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 'var(--tertiary-navigation-radius-xs, 4px)',
    borderLeft: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 'var(--tertiary-navigation-radius-xs, 4px)',
    boxSizing: 'border-box',
    color: useAccentColors ? theme.palette.common.white : colors.text,
    height: '100%',
    minHeight: 'unset',
    minWidth: 'unset',
    opacity: 1,
    overflow: 'hidden',
    padding: 0,
    pointerEvents: isActionState ? 'none' : undefined,
    cursor: isFinalAllocationLocked ? 'not-allowed' : 'pointer',
    transition: theme.transitions.create(['background-color', 'border-color', 'color', 'opacity', 'width'], {
      duration: 240,
      easing: theme.transitions.easing.easeInOut,
    }),
    width: 'var(--tertiary-navigation-action-height, 24px)',
    '& svg': {
      fontSize: 'var(--tertiary-navigation-icon-size, 16px)',
    },
    '&:hover': {
      backgroundColor: useAccentColors ? accentHoverColors.backgroundColor : colors.hoverBackground,
      borderColor: useAccentColors ? accentHoverColors.borderColor : colors.hoverBorder,
      color: useAccentColors ? theme.palette.common.white : ('hoverText' in colors && colors.hoverText) || colors.text,
    },
    '&.Mui-disabled': {
      backgroundColor: disabledColors.backgroundColor,
      borderColor: disabledColors.borderColor,
      color: disabledColors.color,
      opacity: NAVIGATION_ACTION_DISABLED_OPACITY,
    },
  };
});

export const TertiaryNavigationSplitMainContent = styled('span')(({ theme }) => ({
  alignItems: 'center',
  display: 'inline-flex',
  gap: 'var(--tertiary-navigation-gap-xs, 4px)',
  height: 'var(--tertiary-navigation-line-height, 16px)',
  justifyContent: 'center',
  minWidth: 0,
  overflow: 'hidden',
  position: 'relative',
  transition: theme.transitions.create(['width'], {
    duration: 240,
    easing: theme.transitions.easing.easeInOut,
  }),
}));

export const TertiaryNavigationSplitContentItem = styled('span', {
  shouldForwardProp: shouldForwardSplitContentItemProp,
})<SplitContentItemStyleProps>(({ contentPosition, theme }) => {
  const contentTranslateY = (() => {
    if (contentPosition === 'active') {
      return 0;
    }

    if (contentPosition === 'next') {
      return 'calc(var(--tertiary-navigation-line-height, 16px) * -1)';
    }

    return 'var(--tertiary-navigation-line-height, 16px)';
  })();

  return {
    alignItems: 'center',
    display: 'inline-flex',
    gap: 'var(--tertiary-navigation-gap-xs, 4px)',
    justifyContent: 'center',
    left: '50%',
    lineHeight: 'var(--tertiary-navigation-line-height, 16px)',
    opacity: contentPosition === 'active' ? 1 : 0,
    position: 'absolute',
    top: '50%',
    transform: `translate(-50%, -50%) translateY(${contentTranslateY})`,
    transition: theme.transitions.create(['opacity', 'transform'], {
      duration: 260,
      easing: theme.transitions.easing.easeInOut,
    }),
    whiteSpace: 'nowrap',
  };
});

export const TertiaryNavigationSplitStatusIcon = styled('span')(({ theme }) => ({
  alignItems: 'center',
  color: 'currentColor',
  display: 'inline-flex',
  flex: '0 0 auto',
  fontSize: 'var(--tertiary-navigation-icon-size, 16px)',
  height: 'var(--tertiary-navigation-icon-size, 16px)',
  justifyContent: 'center',
  opacity: 0.95,
  transformOrigin: 'center',
  transition: theme.transitions.create(['opacity', 'transform'], {
    duration: 160,
    easing: theme.transitions.easing.easeInOut,
  }),
  width: 'var(--tertiary-navigation-icon-size, 16px)',
}));

export const TertiaryNavigationSplitSavedCheck = styled('svg')({
  display: 'block',
  height: 'var(--tertiary-navigation-icon-size, 16px)',
  overflow: 'visible',
  width: 'var(--tertiary-navigation-icon-size, 16px)',
});

export const TertiaryNavigationSplitSavedCheckPath = styled('path')({
  animation: `${tertiaryNavigationSavedCheckDraw} 420ms ease-out 80ms both`,
  fill: 'none',
  stroke: 'currentColor',
  strokeDasharray: 1,
  strokeDashoffset: 1,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  strokeWidth: 2,
});
